import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3, Play, Pause, Plus, Link, Unlink,
  Target, DollarSign, Calendar, CalendarCheck,
  Text, ChevronDown, AlertCircle, CheckCircle,
  RefreshCw, TrendingUp, Eye, MousePointer, Database, Trash2, Settings, Edit,
  ArrowLeft, ArrowRight, UploadCloud, Globe, Laptop, Smartphone,
  Users, Sliders, Layers, Tv, EyeOff, Facebook, Instagram
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://rnd.kalptree.xyz/api';

const META_OBJECTIVES = ['Website Traffic', 'Lead Generation', 'Sales'];

const INITIAL_CAMPAIGN = {
  name: '',
  objective: 'Website Traffic',
  dailyBudget: '',
  lifetimeBudget: '',
  budgetType: 'daily',
  startDate: '',
  endDate: '',
  country: 'US',
  minAge: 18,
  maxAge: 65,
  destinationUrl: '',
  primaryText: '',
  headline: '',
  description: '',
  cta: 'Learn More',
  pageId: '',
  instagramAccountId: '',
  pixelId: '',
  // New step-based fields
  conversionLocation: 'website',
  genders: 'all',
  languages: '',
  interests: '',
  placements: 'advantage_plus',
  manualPlacements: {
    facebook: true,
    instagram: true,
    messenger: false,
    audienceNetwork: false,
  },
  adSetup: 'new_ad',
  existingPostId: '',
  format: 'single_image',
  trackingEnabled: true,
  imageFile: null,
  imagePreviewUrl: '',
};

const CTAS = ['Learn More', 'Shop Now', 'Sign Up', 'Contact Us', 'Book Now', 'Get Offer', 'Download', 'Subscribe'];

const CONVERSION_LOCATIONS = [
  { id: 'website', label: 'Website', desc: 'Drive traffic and conversions to your site.' },
  { id: 'messenger', label: 'Messenger', desc: 'Start conversational ads in Meta Messenger.' },
  { id: 'instagram_shop', label: 'Instagram Shop', desc: 'Direct shoppers to your IG storefront.' },
  { id: 'phone_calls', label: 'Phone Calls', desc: 'Prompt audience to call your business.' },
];

const AD_FORMATS = [
  { id: 'single_image', label: 'Single Image', desc: 'Clean layout with one visual asset.' },
  { id: 'single_video', label: 'Single Video', desc: 'Dynamic rich video creative.' },
  { id: 'carousel', label: 'Carousel', desc: 'Multiple scrollable images or videos.' },
];

const MetaAdsDashboard = () => {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [form, setForm] = useState(INITIAL_CAMPAIGN);
  const [step, setStep] = useState(1);
  const [previewPlatform, setPreviewPlatform] = useState('facebook'); // 'facebook' or 'instagram'

  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [notification, setNotification] = useState(null);
  const [adAccounts, setAdAccounts] = useState([]);
  const [pages, setPages] = useState([]);
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [credentialForm, setCredentialForm] = useState({ appId: '', appSecret: '' });
  const [savingCredentials, setSavingCredentials] = useState(false);

  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  const apiFetch = useCallback(async (path, options = {}) => {
    let cleanPath = path;
    const cleanBase = API_URL.replace(/\/+$/, '');
    if (cleanBase.endsWith('/api') && cleanPath.startsWith('/api/')) {
      cleanPath = cleanPath.replace(/^\/api/, '');
    }
    const url = `${cleanBase}${cleanPath.startsWith('/') ? '' : '/'}${cleanPath}`;
    const defaultHeaders = {};
    if (options.body && !(options.body instanceof FormData)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }
    const res = await fetch(url, { ...options, headers: { ...defaultHeaders, ...options.headers } });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.detail || data.message || 'Request failed');
    }
    return data;
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const data = await apiFetch('/api/meta/status');
      setConnection(data);
    } catch {
      setConnection({ connected: false });
    }
  }, [apiFetch]);

  const handleExchangeToken = useCallback(async (accessToken, tenantId = null) => {
    setConnecting(true);
    try {
      const data = await apiFetch('/api/meta/exchange-token', {
        method: 'POST',
        body: JSON.stringify({ access_token: accessToken, tenant_id: tenantId }),
      });
      showNotification('Meta account recognized and connected!', 'success');
      await loadStatus();
      return data;
    } catch (err) {
      showNotification(err.message, 'error');
      throw err;
    } finally {
      setConnecting(false);
    }
  }, [apiFetch, showNotification, loadStatus]);

  const loadCampaigns = useCallback(async () => {
    try {
      const data = await apiFetch('/api/meta/campaigns');
      setCampaigns(data.campaigns || []);
    } catch { }
  }, [apiFetch]);

  const loadCredentialsStatus = useCallback(async () => {
    try {
      const data = await apiFetch('/api/meta/credentials-status');
      setCredentials(data);
      if (data.has_dynamic) {
        setCredentialForm({ appId: '', appSecret: '' });
      }
    } catch { }
  }, [apiFetch]);

  useEffect(() => {
    Promise.all([loadStatus(), loadCampaigns(), loadCredentialsStatus()]).finally(() => setLoading(false));
  }, [loadStatus, loadCampaigns, loadCredentialsStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const data = await apiFetch('/api/meta/auth-url');
      if (data.connected) {
        showNotification('Already connected.', 'info');
        loadStatus();
      } else if (data.auth_url) {
        window.location.href = data.auth_url;
      }
    } catch (err) {
      showNotification(err.message, 'error');
    }
    setConnecting(false);
  };

  const handleDisconnect = async () => {
    try {
      await apiFetch('/api/meta/disconnect', { method: 'POST' });
      setConnection({ connected: false });
      setCampaigns([]);
      showNotification('Meta Ads disconnected.', 'info');
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const loadAccounts = async () => {
    try {
      const data = await apiFetch('/api/meta/accounts');
      setAdAccounts(data.ad_accounts || []);
      setPages(data.pages || []);
      if (data.ad_accounts?.length && !connection?.ad_account_id) {
        setForm(prev => ({ ...prev, pageId: data.pages?.[0]?.id || '' }));
      }
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const loadInstagramAccounts = async (pageId) => {
    if (!pageId) return;
    try {
      const data = await apiFetch(`/api/meta/instagram-accounts?page_id=${pageId}`);
      setInstagramAccounts(data.instagram_accounts || []);
    } catch { }
  };

  const handleSelectAccount = async () => {
    const selected = adAccounts.find(a => a.id === connection?.ad_account_id);
    const selectedPage = pages.find(p => p.id === connection?.page_id);
    try {
      const fd = new FormData();
      fd.append('ad_account_id', connection?.ad_account_id || adAccounts[0]?.id);
      fd.append('ad_account_name', selected?.name || '');
      fd.append('page_id', connection?.page_id || pages[0]?.id || '');
      fd.append('page_name', selectedPage?.name || '');
      await apiFetch('/api/meta/select-account', { method: 'POST', body: fd });
      loadStatus();
      loadInstagramAccounts(connection?.page_id || pages[0]?.id);
      showNotification('Account settings saved.', 'success');
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleSelectInstagram = async (id, username) => {
    try {
      const fd = new FormData();
      fd.append('instagram_account_id', id);
      fd.append('instagram_username', username);
      await apiFetch('/api/meta/select-instagram', { method: 'POST', body: fd });
      loadStatus();
      showNotification('Instagram account selected.', 'success');
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'pageId' && value) {
      loadInstagramAccounts(value);
    }
  };

  const handleManualPlacementChange = (platform, checked) => {
    setForm(prev => ({
      ...prev,
      manualPlacements: {
        ...prev.manualPlacements,
        [platform]: checked
      }
    }));
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm(prev => ({
        ...prev,
        imageFile: file,
        imagePreviewUrl: URL.createObjectURL(file)
      }));
    }
  };

  const handleRemoveImage = () => {
    setForm(prev => ({
      ...prev,
      imageFile: null,
      imagePreviewUrl: ''
    }));
  };

  const compileFormData = () => {
    const fd = new FormData();
    fd.append('name', form.name);
    fd.append('objective', form.objective);
    fd.append('budget_type', form.budgetType);
    if (form.budgetType === 'daily') {
      fd.append('daily_budget', form.dailyBudget);
    } else {
      fd.append('lifetime_budget', form.lifetimeBudget);
    }
    fd.append('start_date', form.startDate);
    if (form.endDate) fd.append('end_date', form.endDate);
    fd.append('country', form.country);
    fd.append('min_age', String(form.minAge));
    fd.append('max_age', String(form.maxAge));
    fd.append('destination_url', form.destinationUrl);
    fd.append('primary_text', form.primaryText);
    fd.append('headline', form.headline);
    fd.append('description', form.description);
    fd.append('cta', form.cta);
    if (form.pageId) fd.append('page_id', form.pageId);
    if (form.instagramAccountId) fd.append('instagram_account_id', form.instagramAccountId);
    if (form.pixelId) fd.append('pixel_id', form.pixelId);

    // Step fields
    fd.append('conversion_location', form.conversionLocation);
    fd.append('genders', form.genders);
    fd.append('languages', form.languages);
    fd.append('interests', form.interests);
    fd.append('placements', form.placements);

    const manualPlacementsList = Object.keys(form.manualPlacements)
      .filter(k => form.manualPlacements[k])
      .map(k => k === 'audienceNetwork' ? 'audience_network' : k)
      .join(',');
    fd.append('manual_placements', manualPlacementsList);

    fd.append('ad_setup', form.adSetup);
    if (form.adSetup === 'existing_post') {
      fd.append('existing_post_id', form.existingPostId);
    }
    fd.append('format', form.format);
    fd.append('tracking_enabled', String(form.trackingEnabled));
    if (form.imageFile) {
      fd.append('image', form.imageFile);
    }
    return fd;
  };

  const handleCreateCampaign = async () => {
    const fd = compileFormData();
    try {
      const url = editingCampaignId ? `/api/meta/campaigns/${editingCampaignId}` : '/api/meta/campaigns';
      const data = await apiFetch(url, { method: 'POST', body: fd });
      if (editingCampaignId) {
        setCampaigns(prev => prev.map(c => c.id === editingCampaignId ? data.campaign : c));
      } else {
        setCampaigns(prev => [data.campaign, ...prev]);
      }
      setShowCreateForm(false);
      setEditingCampaignId(null);
      setForm(INITIAL_CAMPAIGN);
      setStep(1);
      showNotification(editingCampaignId ? 'Campaign draft updated!' : 'Campaign draft saved!', 'success');
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handlePublish = async (id) => {
    try {
      const data = await apiFetch(`/api/meta/campaigns/${id}/publish`, { method: 'POST' });
      setCampaigns(prev => prev.map(c => c.id === id ? data.campaign : c));
      showNotification('Campaign published (PAUSED) to Meta.', 'success');
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleSaveAndPublish = async () => {
    const fd = compileFormData();
    try {
      // 1. Create or Update locally first
      const url = editingCampaignId ? `/api/meta/campaigns/${editingCampaignId}` : '/api/meta/campaigns';
      const data = await apiFetch(url, { method: 'POST', body: fd });
      if (editingCampaignId) {
        setCampaigns(prev => prev.map(c => c.id === editingCampaignId ? data.campaign : c));
      } else {
        setCampaigns(prev => [data.campaign, ...prev]);
      }
      // 2. Publish right away
      await handlePublish(data.campaign.id);
      setShowCreateForm(false);
      setEditingCampaignId(null);
      setForm(INITIAL_CAMPAIGN);
      setStep(1);
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleTogglePause = async (id) => {
    const c = campaigns.find(c => c.id === id);
    const isActive = c?.status === 'active';
    try {
      const data = await apiFetch(`/api/meta/campaigns/${id}/${isActive ? 'pause' : 'resume'}`, { method: 'POST' });
      setCampaigns(prev => prev.map(c => c.id === id ? data.campaign : c));
      showNotification(`Campaign ${isActive ? 'paused' : 'resumed'}.`, 'success');
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiFetch(`/api/meta/campaigns/${id}/delete`, { method: 'POST' });
      setCampaigns(prev => prev.filter(c => c.id !== id));
      showNotification('Campaign archived on Meta and deleted locally.', 'info');
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleRefreshMetrics = async (id) => {
    try {
      const data = await apiFetch(`/api/meta/campaigns/${id}/metrics`);
      setCampaigns(prev => prev.map(c => c.id === id ? { ...c, ...data.metrics } : c));
      showNotification('Metrics refreshed from Meta.', 'success');
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleOpenCreate = () => {
    setEditingCampaignId(null);
    setForm(INITIAL_CAMPAIGN);
    setShowCreateForm(true);
    setStep(1);
    loadAccounts();
  };

  const handleEditClick = (campaign) => {
    const manualPlacementsObj = {
      facebook: false,
      instagram: false,
      messenger: false,
      audienceNetwork: false,
    };
    if (campaign.manual_placements) {
      campaign.manual_placements.split(',').forEach(p => {
        const key = p.trim() === 'audience_network' ? 'audienceNetwork' : p.trim();
        if (key in manualPlacementsObj) {
          manualPlacementsObj[key] = true;
        }
      });
    }

    setForm({
      name: campaign.name || '',
      objective: campaign.objective || 'Website Traffic',
      dailyBudget: campaign.daily_budget !== null && campaign.daily_budget !== undefined ? String(campaign.daily_budget) : '',
      lifetimeBudget: campaign.lifetime_budget !== null && campaign.lifetime_budget !== undefined ? String(campaign.lifetime_budget) : '',
      budgetType: campaign.budget_type || 'daily',
      startDate: campaign.start_date || '',
      endDate: campaign.end_date || '',
      country: campaign.country || 'US',
      minAge: campaign.min_age || 18,
      maxAge: campaign.max_age || 65,
      destinationUrl: campaign.destination_url || '',
      primaryText: campaign.primary_text || '',
      headline: campaign.headline || '',
      description: campaign.description || '',
      cta: campaign.cta || 'Learn More',
      pageId: campaign.page_id || '',
      instagramAccountId: campaign.instagram_account_id || '',
      pixelId: campaign.pixel_id || '',
      conversionLocation: campaign.conversion_location || 'website',
      genders: campaign.genders || 'all',
      languages: campaign.languages || '',
      interests: campaign.interests || '',
      placements: campaign.placements || 'advantage_plus',
      manualPlacements: manualPlacementsObj,
      adSetup: campaign.ad_setup || 'new_ad',
      existingPostId: campaign.existing_post_id || '',
      format: campaign.format || 'single_image',
      trackingEnabled: campaign.tracking_enabled !== undefined ? campaign.tracking_enabled : true,
      imageFile: null,
      imagePreviewUrl: campaign.image_url ? `${API_URL}${campaign.image_url}` : ''
    });

    setEditingCampaignId(campaign.id);
    setShowCreateForm(true);
    setStep(1);
    loadAccounts();
  };

  const handleOpenSettings = () => {
    setShowSettings(true);
    loadAccounts();
  };

  const handleSaveCredentials = async () => {
    if (!credentialForm.appId || !credentialForm.appSecret) {
      showNotification('Please enter both App ID and App Secret.', 'error');
      return;
    }
    setSavingCredentials(true);
    try {
      const fd = new FormData();
      fd.append('app_id', credentialForm.appId.trim());
      fd.append('app_secret', credentialForm.appSecret.trim());
      await apiFetch('/api/meta/credentials', { method: 'POST', body: fd });
      showNotification('Meta App credentials saved!', 'success');
      setCredentialForm({ appId: '', appSecret: '' });
      loadCredentialsStatus();
    } catch (err) {
      showNotification(err.message, 'error');
    }
    setSavingCredentials(false);
  };

  const tabs = [
    { id: 'campaigns', label: 'Campaigns', icon: BarChart3 },
    { id: 'create', label: 'Create Campaign', icon: Plus },
    { id: 'metrics', label: 'Metrics', icon: TrendingUp },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="w-2 h-2 bg-purple-500 rounded-full animate-ping mr-3" />
        <span className="text-sm uppercase tracking-widest">Loading...</span>
      </div>
    );
  }

  const isConnected = connection?.connected;
  const hasCredentials = credentials?.configured;

  const getStepValidationErrors = (stepNum) => {
    const errs = [];
    if (stepNum === 1) {
      if (!form.name.trim()) errs.push('Campaign name is required.');
      if (form.budgetType === 'daily' && (!form.dailyBudget || parseFloat(form.dailyBudget) <= 0)) {
        errs.push('Daily Budget must be a positive number.');
      }
      if (form.budgetType === 'lifetime' && (!form.lifetimeBudget || parseFloat(form.lifetimeBudget) <= 0)) {
        errs.push('Lifetime Budget must be a positive number.');
      }
      if (!form.startDate) errs.push('Start date is required.');
      if (form.endDate && form.endDate < form.startDate) errs.push('End date cannot be before start date.');
      if (!form.country.trim()) errs.push('Target country is required.');
    } else if (stepNum === 2) {
      if (!form.pageId) errs.push('Facebook Page is required.');
      if (form.adSetup === 'new_ad') {
        if (!form.destinationUrl.trim()) errs.push('Destination URL is required.');
        if (!form.primaryText.trim()) errs.push('Primary Text is required.');
        if (!form.headline.trim()) errs.push('Headline is required.');
      } else {
        if (!form.existingPostId.trim()) errs.push('Existing Post ID is required.');
      }
    }
    return errs;
  };

  const validateAndProceed = (nextStep) => {
    const errs = getStepValidationErrors(step);
    if (errs.length > 0) {
      showNotification(errs[0], 'error');
    } else {
      setStep(nextStep);
    }
  };

  const renderCredentialForm = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-900/50 border border-purple-500/30 rounded-xl p-5 space-y-4"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings size={16} className="text-purple-400" />
          <h3 className="text-sm font-bold text-gray-200">Meta App Credentials</h3>
        </div>
        <button
          type="button"
          onClick={() => setCredentialForm({ appId: '1036490438871684', appSecret: '8fcfd70554dc5e518f98a7f1c074db42' })}
          className="text-xs px-2 py-1 rounded bg-gray-800 text-gray-400 hover:text-gray-200 border border-gray-700 font-medium transition-all"
        >
          Auto Fill
        </button>
      </div>
      <p className="text-xs text-gray-500">
        Enter your Meta App ID and App Secret from{' '}
        <a href="https://developers.facebook.com" target="_blank" rel="noreferrer" className="text-blue-400 underline">developers.facebook.com</a>
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">App ID</label>
          <input
            type="text"
            value={credentialForm.appId}
            onChange={(e) => setCredentialForm(prev => ({ ...prev, appId: e.target.value }))}
            className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-purple-500 outline-none"
            placeholder="e.g. 1234567890123456"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">App Secret</label>
          <input
            type="password"
            value={credentialForm.appSecret}
            onChange={(e) => setCredentialForm(prev => ({ ...prev, appSecret: e.target.value }))}
            className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-purple-500 outline-none"
            placeholder="Enter your app secret"
          />
        </div>
      </div>
      <button
        onClick={handleSaveCredentials}
        disabled={savingCredentials || !credentialForm.appId || !credentialForm.appSecret}
        className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-2.5 rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {savingCredentials ? 'Saving...' : 'Save Credentials'}
      </button>
    </motion.div>
  );

  const renderConnectionStatus = () => (
    <div className={`p-4 rounded-xl border ${isConnected ? 'bg-green-900/10 border-green-500/30' : 'bg-gray-800/30 border-gray-700/50'}`}>
      <div className="space-y-3">
        {!isConnected && renderCredentialForm()}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isConnected ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-gray-500" />
            )}
            <div>
              <p className="text-sm font-bold text-gray-200">
                {isConnected ? 'Meta Ads Connected' : (hasCredentials ? 'Not Connected' : 'Enter credentials above')}
              </p>
              {isConnected && (
                <div className="text-xs text-gray-400 space-y-0.5">
                  <p>{connection.ad_account_name || connection.ad_account_id || 'No ad account selected'}</p>
                  {connection.page_name && <p>Page: {connection.page_name}</p>}
                  {connection.instagram_account_username && <p>Instagram: @{connection.instagram_account_username}</p>}
                </div>
              )}
              {!isConnected && hasCredentials && (
                <p className="text-xs text-gray-500">App ID: {credentials?.app_id ? `${credentials.app_id.slice(0, 4)}...${credentials.app_id.slice(-4)}` : ''}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isConnected && (
              <button
                onClick={handleOpenSettings}
                className="text-xs px-3 py-2 rounded-lg bg-gray-800 text-gray-400 hover:text-gray-200 border border-gray-700 font-medium flex items-center gap-1.5 transition-all"
              >
                <Settings size={14} /> Settings
              </button>
            )}
            {hasCredentials && (
              <button
                onClick={isConnected ? handleDisconnect : handleConnect}
                disabled={connecting}
                className={`text-xs px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${isConnected
                  ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30 border border-red-500/30'
                  : 'bg-blue-600 text-white hover:bg-blue-500 border border-blue-500 disabled:opacity-50'
                  }`}
              >
                {connecting ? 'Connecting...' : isConnected ? <><Unlink size={14} /> Disconnect</> : <><Link size={14} /> Connect</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettingsPanel = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-900/50 border border-gray-700/50 rounded-xl p-6 space-y-5 animate-all"
    >
      <h3 className="text-lg font-bold text-white flex items-center gap-2">
        <Settings size={18} className="text-purple-400" /> Account Settings
      </h3>

      {hasCredentials && (
        <div className="bg-gray-950/50 border border-gray-700/30 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Update Meta App Credentials</h4>
            <button
              type="button"
              onClick={() => setCredentialForm({ appId: '1036490438871684', appSecret: '8fcfd70554dc5e518f98a7f1c074db42' })}
              className="text-xs px-2 py-1 rounded bg-gray-800 text-gray-400 hover:text-gray-200 border border-gray-700 font-medium transition-all"
            >
              Auto Fill
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">App ID</label>
              <input
                type="text"
                value={credentialForm.appId}
                onChange={(e) => setCredentialForm(prev => ({ ...prev, appId: e.target.value }))}
                className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-purple-500 outline-none"
                placeholder={credentials?.app_id ? `${credentials.app_id.slice(0, 4)}...${credentials.app_id.slice(-4)}` : ''}
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">App Secret</label>
              <input
                type="password"
                value={credentialForm.appSecret}
                onChange={(e) => setCredentialForm(prev => ({ ...prev, appSecret: e.target.value }))}
                className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-purple-500 outline-none"
                placeholder="Enter new app secret"
              />
            </div>
          </div>
          <button
            onClick={handleSaveCredentials}
            disabled={savingCredentials || !credentialForm.appId || !credentialForm.appSecret}
            className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-2 rounded-lg transition-all disabled:opacity-50 text-sm"
          >
            {savingCredentials ? 'Updating...' : 'Update Credentials'}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Ad Account</label>
          <select
            value={connection?.ad_account_id || ''}
            onChange={(e) => setConnection(prev => ({ ...prev, ad_account_id: e.target.value }))}
            className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-purple-500 outline-none"
          >
            <option value="">Select an ad account</option>
            {adAccounts.map(a => (
              <option key={a.id} value={a.id}>{a.name} ({a.account_id})</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Facebook Page</label>
          {pages.length > 0 ? (
            <select
              value={connection?.page_id || ''}
              onChange={(e) => {
                setConnection(prev => ({ ...prev, page_id: e.target.value }));
                loadInstagramAccounts(e.target.value);
              }}
              className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-purple-500 outline-none"
            >
              <option value="">Select a Page</option>
              {pages.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={connection?.page_id || ''}
              onChange={(e) => {
                setConnection(prev => ({ ...prev, page_id: e.target.value }));
                loadInstagramAccounts(e.target.value);
              }}
              className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-purple-500 outline-none"
              placeholder="No pages found. Enter Page ID manually"
            />
          )}
        </div>

        {instagramAccounts.length > 0 && (
          <div>
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Instagram Account</label>
            <select
              value={connection?.instagram_account_id || ''}
              onChange={(e) => {
                const acct = instagramAccounts.find(a => a.id === e.target.value);
                handleSelectInstagram(e.target.value, acct?.name || acct?.id);
              }}
              className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-purple-500 outline-none"
            >
              <option value="">Select Instagram account</option>
              {instagramAccounts.map(ig => (
                <option key={ig.id} value={ig.id}>{ig.name || ig.id}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <button
          onClick={handleSelectAccount}
          className="flex-1 bg-purple-600 hover:bg-purple-500 text-white font-bold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2"
        >
          Save Settings
        </button>
      </div>
    </motion.div>
  );

  const renderStepTracker = () => {
    const steps = [
      { num: 1, label: '1. Ad Set Logic', icon: Sliders },
      { num: 2, label: '2. Ad Creative', icon: Layers },
      { num: 3, label: '3. Review & Publish', icon: CheckCircle }
    ];
    return (
      <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-800">
        {steps.map((s, idx) => (
          <React.Fragment key={s.num}>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${step === s.num
                ? 'bg-gradient-to-br from-blue-600 to-purple-600 text-white ring-2 ring-purple-500/50'
                : step > s.num
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-800 text-gray-400'
                }`}>
                {step > s.num ? <CheckCircle size={16} /> : s.num}
              </div>
              <span className={`text-xs font-bold hidden sm:inline ${step === s.num ? 'text-white' : 'text-gray-500'}`}>
                {s.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-4 rounded transition-all duration-300 ${step > s.num ? 'bg-green-600' : 'bg-gray-800'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderCampaignForm = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-900/50 border border-gray-700/50 rounded-xl p-6 space-y-6"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          {editingCampaignId ? (
            <Edit size={18} className="text-indigo-400" />
          ) : (
            <Plus size={18} className="text-blue-400" />
          )}
          {editingCampaignId ? 'Edit Meta Campaign' : 'New Meta Campaign'}
        </h3>
        <button
          type="button"
          onClick={() => {
            const today = new Date().toISOString().split('T')[0];
            const nextMonth = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            setForm({
              ...INITIAL_CAMPAIGN,
              name: 'Summer Sale 2026',
              objective: 'Sales',
              dailyBudget: '50',
              startDate: today,
              endDate: nextMonth,
              country: 'US',
              minAge: 18,
              maxAge: 65,
              destinationUrl: 'https://example.com/summer-sale',
              primaryText: 'Don\'t miss out on our biggest sale of the year! Shop now and save up to 50% on select items.',
              headline: 'Summer Sale - Up to 50% Off',
              description: 'Limited time offer. Shop the collection today.',
              cta: 'Shop Now',
              pageId: pages[0]?.id || '',
              instagramAccountId: instagramAccounts[0]?.id || '',
              pixelId: '123456789012345',
              conversionLocation: 'website',
              genders: 'all',
              languages: 'english',
              interests: 'shopping',
              placements: 'advantage_plus',
            });
          }}
          className="text-xs px-3 py-2 rounded-lg bg-gray-800 text-gray-400 hover:text-gray-200 border border-gray-700 font-medium flex items-center gap-1.5 transition-all"
        >
          Auto Fill
        </button>
      </div>

      {renderStepTracker()}

      {/* STEP 1: CONFIGURE AD SET (THE LOGIC) */}
      {step === 1 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Campaign Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none"
                placeholder="e.g. Summer Sale 2026"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Objective</label>
              <select
                value={form.objective}
                onChange={(e) => handleFormChange('objective', e.target.value)}
                className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none"
              >
                {META_OBJECTIVES.map(obj => (
                  <option key={obj} value={obj}>{obj}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Conversion Location</label>
              <select
                value={form.conversionLocation}
                onChange={(e) => handleFormChange('conversionLocation', e.target.value)}
                className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none"
              >
                {CONVERSION_LOCATIONS.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Conversion Location cards indicator */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {CONVERSION_LOCATIONS.map(loc => (
              <div
                key={loc.id}
                onClick={() => handleFormChange('conversionLocation', loc.id)}
                className={`p-4 rounded-xl border cursor-pointer transition-all ${form.conversionLocation === loc.id
                  ? 'bg-blue-900/10 border-blue-500/50 text-white shadow-lg'
                  : 'bg-gray-950/40 border-gray-800 text-gray-400 hover:border-gray-700'
                  }`}
              >
                <div className="font-bold text-xs mb-1">{loc.label}</div>
                <div className="text-[10px] text-gray-500">{loc.desc}</div>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-800/60 pt-5 space-y-4">
            <h4 className="text-sm font-bold text-gray-300 flex items-center gap-1.5">
              <DollarSign size={16} className="text-blue-400" /> Budget & Schedule
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Budget Type</label>
                <select
                  value={form.budgetType}
                  onChange={(e) => handleFormChange('budgetType', e.target.value)}
                  className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none"
                >
                  <option value="daily">Daily Budget</option>
                  <option value="lifetime">Lifetime Budget</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">
                  {form.budgetType === 'daily' ? 'Daily Amount ($)' : 'Lifetime Amount ($)'}
                </label>
                <input
                  type="number"
                  value={form.budgetType === 'daily' ? form.dailyBudget : form.lifetimeBudget}
                  onChange={(e) => handleFormChange(form.budgetType === 'daily' ? 'dailyBudget' : 'lifetimeBudget', e.target.value)}
                  className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none"
                  placeholder="50"
                  min="1"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Start Date</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => handleFormChange('startDate', e.target.value)}
                    className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-xs text-gray-200 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">End Date (Opt)</label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => handleFormChange('endDate', e.target.value)}
                    className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-xs text-gray-200 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800/60 pt-5 space-y-4">
            <h4 className="text-sm font-bold text-gray-300 flex items-center gap-1.5">
              <Users size={16} className="text-blue-400" /> Audience Targeting
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Country / Region</label>
                <input
                  type="text"
                  value={form.country}
                  onChange={(e) => handleFormChange('country', e.target.value)}
                  className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none"
                  placeholder="US"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Min Age</label>
                  <input
                    type="number"
                    value={form.minAge}
                    onChange={(e) => handleFormChange('minAge', parseInt(e.target.value) || 18)}
                    className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none"
                    min="13"
                    max="65"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Max Age</label>
                  <input
                    type="number"
                    value={form.maxAge}
                    onChange={(e) => handleFormChange('maxAge', parseInt(e.target.value) || 65)}
                    className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none"
                    min="18"
                    max="65"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Genders</label>
                <div className="flex bg-gray-950 border border-gray-700 rounded-lg p-1">
                  {['all', 'men', 'women'].map(g => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => handleFormChange('genders', g)}
                      className={`flex-1 py-1.5 text-xs font-bold rounded-md capitalize transition-all ${form.genders === g ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                        }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Languages (Opt)</label>
                <input
                  type="text"
                  value={form.languages}
                  onChange={(e) => handleFormChange('languages', e.target.value)}
                  className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none"
                  placeholder="e.g. English, Spanish"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Detailed Interests (Opt)</label>
                <input
                  type="text"
                  value={form.interests}
                  onChange={(e) => handleFormChange('interests', e.target.value)}
                  className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none"
                  placeholder="e.g. shopping, fitness (comma-separated)"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800/60 pt-5 space-y-4">
            <h4 className="text-sm font-bold text-gray-300 flex items-center gap-1.5">
              <Tv size={16} className="text-blue-400" /> Placements
            </h4>
            <div className="flex bg-gray-950 border border-gray-700 rounded-lg p-1.5 max-w-md">
              <button
                type="button"
                onClick={() => handleFormChange('placements', 'advantage_plus')}
                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${form.placements === 'advantage_plus' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                  }`}
              >
                Advantage+ Placements
              </button>
              <button
                type="button"
                onClick={() => handleFormChange('placements', 'manual')}
                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${form.placements === 'manual' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                  }`}
              >
                Manual Placements
              </button>
            </div>

            {form.placements === 'manual' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="grid grid-cols-2 gap-3 max-w-lg p-3 bg-gray-950 rounded-xl border border-gray-800"
              >
                {Object.keys(form.manualPlacements).map(platform => (
                  <label key={platform} className="flex items-center gap-3 cursor-pointer p-1">
                    <input
                      type="checkbox"
                      checked={form.manualPlacements[platform]}
                      onChange={(e) => handleManualPlacementChange(platform, e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded focus:ring-blue-500"
                    />
                    <span className="text-xs font-medium text-gray-300 capitalize">
                      {platform === 'audienceNetwork' ? 'Audience Network' : platform}
                    </span>
                  </label>
                ))}
              </motion.div>
            )}
          </div>

          <div className="flex justify-between border-t border-gray-800 pt-5">
            <button
              onClick={() => { setShowCreateForm(false); setForm(INITIAL_CAMPAIGN); setEditingCampaignId(null); }}
              className="px-5 py-2.5 rounded-lg text-sm text-gray-400 hover:text-white border border-gray-700 transition"
            >
              Cancel
            </button>
            <button
              onClick={() => validateAndProceed(2)}
              className="px-5 py-2.5 rounded-lg text-sm font-bold bg-blue-600 hover:bg-blue-500 text-white flex items-center gap-2 transition"
            >
              Next Step <ArrowRight size={14} />
            </button>
          </div>
        </motion.div>
      )}

      {/* STEP 2: CREATE YOUR AD (THE CREATIVE) */}
      {step === 2 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Facebook Page</label>
              {pages.length > 0 ? (
                <select
                  value={form.pageId}
                  onChange={(e) => handleFormChange('pageId', e.target.value)}
                  className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none animate-all"
                >
                  <option value="">Select a Page</option>
                  {pages.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={form.pageId}
                  onChange={(e) => handleFormChange('pageId', e.target.value)}
                  className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none"
                  placeholder="Enter Facebook Page ID"
                />
              )}
            </div>

            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Instagram Account (Optional)</label>
              <select
                value={form.instagramAccountId}
                onChange={(e) => handleFormChange('instagramAccountId', e.target.value)}
                className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none animate-all"
              >
                <option value="">None</option>
                {instagramAccounts.map(ig => (
                  <option key={ig.id} value={ig.id}>{ig.name || ig.id}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="border-t border-gray-800/60 pt-5 space-y-4">
            <h4 className="text-sm font-bold text-gray-300">Ad Setup</h4>
            <div className="flex bg-gray-950 border border-gray-700 rounded-lg p-1.5 max-w-md">
              <button
                type="button"
                onClick={() => handleFormChange('adSetup', 'new_ad')}
                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${form.adSetup === 'new_ad' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                  }`}
              >
                Create New Ad
              </button>
              <button
                type="button"
                onClick={() => handleFormChange('adSetup', 'existing_post')}
                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${form.adSetup === 'existing_post' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                  }`}
              >
                Use Existing Post
              </button>
            </div>

            {form.adSetup === 'existing_post' ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-md">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Existing Post ID</label>
                <input
                  type="text"
                  value={form.existingPostId}
                  onChange={(e) => handleFormChange('existingPostId', e.target.value)}
                  className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none"
                  placeholder="e.g. 10156294719284"
                />
              </motion.div>
            ) : (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Ad Format</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {AD_FORMATS.map(fmt => (
                      <div
                        key={fmt.id}
                        onClick={() => handleFormChange('format', fmt.id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${form.format === fmt.id
                          ? 'bg-blue-900/10 border-blue-500/50 text-white'
                          : 'bg-gray-950 border-gray-800 text-gray-400 hover:border-gray-700'
                          }`}
                      >
                        <div className="text-xs font-bold">{fmt.label}</div>
                        <div className="text-[10px] text-gray-500">{fmt.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Media Asset (Image)</label>
                    <div className="relative group">
                      <div className="w-full h-28 bg-gray-950 border-2 border-dashed border-gray-700 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-all">
                        <UploadCloud className="w-8 h-8 text-gray-500 mb-1" />
                        <span className="text-xs text-gray-500 font-medium">Click to upload ad image</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageFileChange}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                      </div>
                    </div>

                    {form.imagePreviewUrl && (
                      <div className="relative mt-3 inline-block">
                        <img
                          src={form.imagePreviewUrl}
                          alt="Ad Creative Preview"
                          className="w-40 h-24 object-cover rounded-lg border border-gray-700"
                        />
                        <button
                          onClick={handleRemoveImage}
                          className="absolute -top-1.5 -right-1.5 bg-red-600 rounded-full p-1 hover:bg-red-500 transition shadow-md"
                        >
                          <Trash2 size={12} className="text-white" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Primary Text</label>
                    <textarea
                      value={form.primaryText}
                      onChange={(e) => handleFormChange('primaryText', e.target.value)}
                      className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none h-20 resize-none"
                      placeholder="Feed ad copy..."
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Headline</label>
                    <input
                      type="text"
                      value={form.headline}
                      onChange={(e) => handleFormChange('headline', e.target.value)}
                      className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none"
                      placeholder="Headline text..."
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Description (Optional)</label>
                    <input
                      type="text"
                      value={form.description}
                      onChange={(e) => handleFormChange('description', e.target.value)}
                      className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none"
                      placeholder="Subheadline info..."
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Call to Action</label>
                    <select
                      value={form.cta}
                      onChange={(e) => handleFormChange('cta', e.target.value)}
                      className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none animate-all"
                    >
                      {CTAS.map(cta => (
                        <option key={cta} value={cta}>{cta}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Destination URL</label>
                    <input
                      type="url"
                      value={form.destinationUrl}
                      onChange={(e) => handleFormChange('destinationUrl', e.target.value)}
                      className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none"
                      placeholder="https://yoursite.com"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          <div className="border-t border-gray-800/60 pt-5 space-y-4">
            <h4 className="text-sm font-bold text-gray-300 flex items-center gap-1.5">
              <CheckCircle size={16} className="text-blue-400" /> Tracking (Meta Pixel)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Dataset / Pixel ID</label>
                <input
                  type="text"
                  value={form.pixelId}
                  onChange={(e) => handleFormChange('pixelId', e.target.value)}
                  className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-blue-500 outline-none"
                  placeholder="e.g. 123456789012345"
                />
              </div>

              <div className="flex items-center gap-3 pt-6">
                <input
                  type="checkbox"
                  id="trackingEnabled"
                  checked={form.trackingEnabled}
                  onChange={(e) => handleFormChange('trackingEnabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded focus:ring-blue-500"
                />
                <label htmlFor="trackingEnabled" className="text-xs font-bold text-gray-400 uppercase tracking-wider cursor-pointer">
                  Enable conversions tracking
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-between border-t border-gray-800 pt-5">
            <button
              onClick={() => setStep(1)}
              className="px-5 py-2.5 rounded-lg text-sm text-gray-400 hover:text-white border border-gray-700 flex items-center gap-1.5 transition"
            >
              <ArrowLeft size={14} /> Back
            </button>
            <button
              onClick={() => validateAndProceed(3)}
              className="px-5 py-2.5 rounded-lg text-sm font-bold bg-blue-600 hover:bg-blue-500 text-white flex items-center gap-2 transition"
            >
              Review & Preview <ArrowRight size={14} />
            </button>
          </div>
        </motion.div>
      )}

      {/* STEP 3: REVIEW & PUBLISH */}
      {step === 3 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left: Summary and Validations */}
            <div className="lg:col-span-6 space-y-4">
              <h4 className="text-sm font-bold text-gray-200">Configuration Summary</h4>

              <div className="bg-gray-950/50 border border-gray-800 rounded-xl p-4 space-y-3 text-xs">
                <div className="flex justify-between border-b border-gray-800 pb-2">
                  <span className="text-gray-500 font-medium">Campaign Name</span>
                  <span className="text-gray-200 font-bold">{form.name}</span>
                </div>
                <div className="flex justify-between border-b border-gray-800 pb-2">
                  <span className="text-gray-500 font-medium">Objective</span>
                  <span className="text-gray-200 font-bold">{form.objective}</span>
                </div>
                <div className="flex justify-between border-b border-gray-800 pb-2">
                  <span className="text-gray-500 font-medium">Location</span>
                  <span className="text-gray-200 font-bold capitalize">{form.conversionLocation.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between border-b border-gray-800 pb-2">
                  <span className="text-gray-500 font-medium">Budget</span>
                  <span className="text-gray-200 font-bold capitalize">
                    {form.budgetType === 'daily' ? `$${form.dailyBudget}/day` : `$${form.lifetimeBudget} total`}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-800 pb-2">
                  <span className="text-gray-500 font-medium">Target Country</span>
                  <span className="text-gray-200 font-bold">{form.country}</span>
                </div>
                <div className="flex justify-between border-b border-gray-800 pb-2">
                  <span className="text-gray-500 font-medium">Ages</span>
                  <span className="text-gray-200 font-bold">{form.minAge}-{form.maxAge}</span>
                </div>
                <div className="flex justify-between border-b border-gray-800 pb-2">
                  <span className="text-gray-500 font-medium">Placements</span>
                  <span className="text-gray-200 font-bold capitalize">
                    {form.placements === 'advantage_plus' ? 'Advantage+' : 'Manual'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 font-medium">Facebook Page</span>
                  <span className="text-gray-200 font-bold truncate max-w-[200px]">
                    {pages.find(p => p.id === form.pageId)?.name || form.pageId}
                  </span>
                </div>
              </div>

              <div className="bg-gray-950/20 border border-gray-800 rounded-xl p-4">
                <h5 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Publish Status Check</h5>
                {getStepValidationErrors(1).concat(getStepValidationErrors(2)).length === 0 ? (
                  <div className="flex items-center gap-2 text-green-400 text-xs font-semibold bg-green-950/20 p-3 rounded-lg border border-green-500/20 animate-pulse">
                    <CheckCircle size={16} /> All mandatory settings are configured. Ready to publish.
                  </div>
                ) : (
                  <div className="space-y-1.5 p-3 bg-red-950/20 border border-red-500/20 rounded-lg text-xs text-red-400">
                    <p className="font-bold mb-1">Please fix errors in previous steps:</p>
                    {getStepValidationErrors(1).concat(getStepValidationErrors(2)).map((err, idx) => (
                      <p key={idx} className="flex items-center gap-1.5"><AlertCircle size={12} /> {err}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Visual Ad Feed Preview */}
            <div className="lg:col-span-6 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-gray-300">Live Ad Preview</span>
                <div className="flex bg-gray-950 border border-gray-800 rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setPreviewPlatform('facebook')}
                    className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded transition-all ${previewPlatform === 'facebook' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-white'
                      }`}
                  >
                    <Facebook size={12} /> Facebook
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewPlatform('instagram')}
                    className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded transition-all ${previewPlatform === 'instagram' ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-md' : 'text-gray-400 hover:text-white'
                      }`}
                  >
                    <Instagram size={12} /> Instagram
                  </button>
                </div>
              </div>

              {previewPlatform === 'facebook' ? (
                // FACEBOOK FEED PREVIEW
                <div className="bg-[#18191a] border border-[#2f3031] text-[#e4e6eb] rounded-xl overflow-hidden shadow-2xl max-w-sm mx-auto">
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center font-bold text-sm text-white">
                        {(pages.find(p => p.id === form.pageId)?.name || 'FB')[0]}
                      </div>
                      <div>
                        <div className="font-bold text-[13px] hover:underline cursor-pointer">
                          {pages.find(p => p.id === form.pageId)?.name || 'Your Brand Name'}
                        </div>
                        <div className="text-[10px] text-[#b0b3b8] flex items-center gap-1">
                          Sponsored · <Globe size={10} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="px-3 pb-2 text-[13px] leading-relaxed whitespace-pre-wrap">
                    {form.primaryText || 'Primary text copy will render here in the Facebook feed.'}
                  </div>

                  <div className="relative border-y border-[#2f3031]">
                    {form.imagePreviewUrl ? (
                      <img src={form.imagePreviewUrl} alt="Ad Visual" className="w-full h-48 object-cover" />
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-gray-900 to-gray-950 flex flex-col items-center justify-center text-gray-600">
                        <UploadCloud size={32} className="mb-1 text-gray-700" />
                        <span className="text-[10px]">Creative media asset placeholder</span>
                      </div>
                    )}
                  </div>

                  <div className="bg-[#242526] p-3 flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] text-[#b0b3b8] uppercase tracking-wider truncate">
                        {form.destinationUrl ? new URL(form.destinationUrl).hostname : 'YOURSITE.COM'}
                      </div>
                      <div className="font-bold text-[14px] leading-snug truncate mt-0.5">
                        {form.headline || 'Big Summer Sale - 50% Off'}
                      </div>
                      {form.description && (
                        <div className="text-[12px] text-[#b0b3b8] mt-0.5 truncate">{form.description}</div>
                      )}
                    </div>
                    <button className="bg-[#3a3b3c] hover:bg-[#4e4f50] text-white font-semibold text-xs px-3.5 py-2 rounded transition shrink-0">
                      {form.cta}
                    </button>
                  </div>
                </div>
              ) : (
                // INSTAGRAM FEED PREVIEW
                <div className="bg-[#000] border border-[#262626] text-[#f5f5f5] rounded-xl overflow-hidden shadow-2xl max-w-sm mx-auto">
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-500 via-pink-500 to-purple-600 p-[1.5px]">
                        <div className="w-full h-full rounded-full bg-black flex items-center justify-center font-bold text-xs">
                          {(pages.find(p => p.id === form.pageId)?.name || 'IG')[0]}
                        </div>
                      </div>
                      <div>
                        <div className="font-bold text-[12px] hover:underline cursor-pointer">
                          {pages.find(p => p.id === form.pageId)?.name ? pages.find(p => p.id === form.pageId).name.toLowerCase().replace(/\s/g, '') : 'your_brand'}
                        </div>
                        <div className="text-[10px] text-[#a8a8a8]">Sponsored</div>
                      </div>
                    </div>
                  </div>

                  <div className="relative aspect-square border-y border-[#262626]">
                    {form.imagePreviewUrl ? (
                      <img src={form.imagePreviewUrl} alt="Ad Visual" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-900 to-gray-950 flex flex-col items-center justify-center text-gray-600">
                        <UploadCloud size={32} className="mb-1 text-gray-700" />
                        <span className="text-[10px]">Creative media asset placeholder</span>
                      </div>
                    )}

                    {/* Instagram CTA Bar on Creative */}
                    <div className="absolute bottom-0 left-0 right-0 bg-[#0095f6] hover:bg-[#1877f2] transition p-2.5 flex items-center justify-between text-white text-xs font-bold cursor-pointer">
                      <span>{form.cta}</span>
                      <ChevronDown size={14} className="-rotate-90" />
                    </div>
                  </div>

                  <div className="p-3 text-[12px] space-y-1">
                    <p className="leading-relaxed">
                      <span className="font-bold mr-1.5">
                        {pages.find(p => p.id === form.pageId)?.name ? pages.find(p => p.id === form.pageId).name.toLowerCase().replace(/\s/g, '') : 'your_brand'}
                      </span>
                      {form.primaryText || 'Instagram post caption text will render here.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between border-t border-gray-800 pt-5">
            <button
              onClick={() => setStep(2)}
              className="px-5 py-2.5 rounded-lg text-sm text-gray-400 hover:text-white border border-gray-700 flex items-center gap-1.5 transition"
            >
              <ArrowLeft size={14} /> Back
            </button>

            <div className="flex gap-2">
              <button
                onClick={handleCreateCampaign}
                disabled={!form.name || getStepValidationErrors(1).concat(getStepValidationErrors(2)).length > 0}
                className="px-5 py-2.5 rounded-lg text-sm text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {editingCampaignId ? 'Update Draft' : 'Save as Draft'}
              </button>

              <button
                onClick={handleSaveAndPublish}
                disabled={getStepValidationErrors(1).concat(getStepValidationErrors(2)).length > 0}
                className="px-6 py-2.5 rounded-lg text-sm font-bold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white flex items-center gap-1.5 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Publish to Meta
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );

  const statusColor = (s) => {
    if (s === 'active') return 'bg-green-500';
    if (s === 'published_paused' || s === 'paused') return 'bg-yellow-500';
    if (s === 'draft') return 'bg-gray-500';
    return 'bg-red-500';
  };

  const renderCampaignCard = (campaign) => {
    const isExpanded = expandedCampaign === campaign.id;
    return (
      <div key={campaign.id} className="bg-gray-900/30 border border-gray-700/50 rounded-xl overflow-hidden transition-all">
        <div
          onClick={() => setExpandedCampaign(isExpanded ? null : campaign.id)}
          className="p-4 cursor-pointer hover:bg-gray-800/30 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`w-2 h-2 rounded-full shrink-0 ${statusColor(campaign.status)}`} />
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-200 truncate">{campaign.name}</p>
                <p className="text-xs text-gray-500">{campaign.objective} · {campaign.status.replace(/_/g, ' ')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-gray-400">
                {campaign.daily_budget ? `$${Number(campaign.daily_budget).toFixed(2)}/day` : campaign.lifetime_budget ? `$${Number(campaign.lifetime_budget).toFixed(2)} total` : ''}
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-gray-800"
            >
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-950 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1 flex items-center justify-center gap-1"><Eye size={12} /> Impressions</p>
                    <p className="text-lg font-bold text-gray-200">{Number(campaign.impressions || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-gray-950 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1 flex items-center justify-center gap-1"><MousePointer size={12} /> Clicks</p>
                    <p className="text-lg font-bold text-gray-200">{Number(campaign.clicks || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-gray-950 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1 flex items-center justify-center gap-1"><DollarSign size={12} /> Spend</p>
                    <p className="text-lg font-bold text-gray-200">${Number(campaign.spend || 0).toFixed(2)}</p>
                  </div>
                </div>

                {campaign.image_url && (
                  <div className="bg-gray-950 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-2 font-semibold">Ad Image Draft</p>
                    <img
                      src={`${API_URL}${campaign.image_url}`}
                      alt="Uploaded Creative"
                      className="w-40 h-24 object-cover rounded-md border border-gray-800"
                    />
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {campaign.status === 'draft' && (
                    <>
                      <button
                        onClick={() => handlePublish(campaign.id)}
                        className="text-xs px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 font-medium flex items-center gap-1.5 transition-all"
                      >
                        <Play size={12} /> Publish to Meta
                      </button>
                      <button
                        onClick={() => handleEditClick(campaign)}
                        className="text-xs px-4 py-2 rounded-lg bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/30 border border-indigo-500/20 font-medium flex items-center gap-1.5 transition-all"
                      >
                        <Edit size={12} /> Edit Draft
                      </button>
                    </>
                  )}
                  {(campaign.status === 'active' || campaign.status === 'paused' || campaign.status === 'published_paused') && (
                    <button
                      onClick={() => handleTogglePause(campaign.id)}
                      className={`text-xs px-4 py-2 rounded-lg font-medium flex items-center gap-1.5 transition-all ${campaign.status === 'active'
                        ? 'bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/30 border border-yellow-500/30'
                        : 'bg-green-600/20 text-green-400 hover:bg-green-600/30 border border-green-500/30'
                        }`}
                    >
                      {campaign.status === 'active' ? <Pause size={12} /> : <Play size={12} />}
                      {campaign.status === 'active' ? 'Pause' : 'Resume'}
                    </button>
                  )}
                  <button
                    onClick={() => handleRefreshMetrics(campaign.id)}
                    className="text-xs px-4 py-2 rounded-lg bg-gray-800 text-gray-400 hover:text-gray-200 font-medium flex items-center gap-1.5 transition-all"
                  >
                    <RefreshCw size={12} /> Refresh Metrics
                  </button>
                  <button
                    onClick={() => handleDelete(campaign.id)}
                    className="text-xs px-4 py-2 rounded-lg bg-red-600/10 text-red-400 hover:bg-red-600/20 font-medium flex items-center gap-1.5 transition-all border border-red-500/20"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>

                <div className="text-xs text-gray-500 space-y-1">
                  <p>Started: {new Date(campaign.start_date).toLocaleDateString()}{campaign.end_date ? ` · Ends: ${new Date(campaign.end_date).toLocaleDateString()}` : ''}</p>
                  <p>Target: {campaign.country} · Ages {campaign.min_age}-{campaign.max_age} · Gender: <span className="capitalize">{campaign.genders || 'All'}</span></p>
                  <p>Languages: {campaign.languages || 'All'} · Interests: {campaign.interests || 'None'}</p>
                  <p>Placements: <span className="capitalize">{campaign.placements === 'advantage_plus' ? 'Advantage+' : 'Manual'}</span></p>
                  <p>CTA: {campaign.cta} · Pixel: {campaign.pixel_id || 'Not configured'}</p>
                  {campaign.provider_campaign_id && (
                    <p>Meta IDs: Campaign={campaign.provider_campaign_id} · Ad Set={campaign.provider_ad_set_id} · Ad={campaign.provider_ad_id}</p>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const renderMetricsTab = () => {
    const totalImpressions = campaigns.reduce((s, c) => s + Number(c.impressions || 0), 0);
    const totalClicks = campaigns.reduce((s, c) => s + Number(c.clicks || 0), 0);
    const totalSpend = campaigns.reduce((s, c) => s + Number(c.spend || 0), 0);
    const activeCampaigns = campaigns.filter(c => c.status === 'active').length;
    const draftCampaigns = campaigns.filter(c => c.status === 'draft').length;

    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-900/30 border border-gray-700/50 rounded-xl p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Database size={14} /> Total Campaigns</p>
            <p className="text-2xl font-bold text-white">{campaigns.length}</p>
          </div>
          <div className="bg-gray-900/30 border border-gray-700/50 rounded-xl p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Play size={14} className="text-green-400" /> Active</p>
            <p className="text-2xl font-bold text-white">{activeCampaigns}</p>
          </div>
          <div className="bg-gray-900/30 border border-gray-700/50 rounded-xl p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Eye size={14} className="text-blue-400" /> Impressions</p>
            <p className="text-2xl font-bold text-white">{totalImpressions.toLocaleString()}</p>
          </div>
          <div className="bg-gray-900/30 border border-gray-700/50 rounded-xl p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><DollarSign size={14} className="text-green-400" /> Total Spend</p>
            <p className="text-2xl font-bold text-white">${totalSpend.toFixed(2)}</p>
          </div>
        </div>

        {campaigns.length > 0 && (
          <div className="bg-gray-900/20 border border-gray-700/50 rounded-xl p-4 flex gap-6 text-sm">
            <span className="text-gray-400">Drafts: <strong className="text-gray-200">{draftCampaigns}</strong></span>
            <span className="text-gray-400">Published: <strong className="text-gray-200">{campaigns.length - draftCampaigns}</strong></span>
            <span className="text-gray-400">CTR: <strong className="text-gray-200">{totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00'}%</strong></span>
            <span className="text-gray-400">CPC: <strong className="text-gray-200">${totalClicks > 0 ? (totalSpend / totalClicks).toFixed(2) : '0.00'}</strong></span>
          </div>
        )}

        {campaigns.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No campaign data yet.</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`p-3 rounded-lg text-sm font-medium flex items-center gap-2 ${notification.type === 'success' ? 'bg-green-900/20 text-green-400 border border-green-500/30' :
              notification.type === 'info' ? 'bg-blue-900/20 text-blue-400 border border-blue-500/30' :
                'bg-red-900/20 text-red-400 border border-red-500/30'
              }`}
          >
            {notification.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            {notification.message}
          </motion.div>
        )}
      </AnimatePresence>

      {renderConnectionStatus()}

      {showSettings && renderSettingsPanel()}

      <div className="flex bg-gray-900 p-1.5 rounded-xl w-fit border border-gray-800 shadow-md">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2.5 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${activeTab === tab.id
              ? 'bg-purple-600 text-white shadow-lg'
              : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
              }`}
          >
            <tab.icon size={16} /> {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-gray-900/20 border border-gray-800 rounded-xl p-6 min-h-[400px]">
        {activeTab === 'campaigns' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Campaigns</h3>
              <button
                onClick={handleOpenCreate}
                className="text-xs px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-500 font-medium flex items-center gap-1.5 transition-all"
              >
                <Plus size={14} /> New Campaign
              </button>
            </div>

            {showCreateForm && renderCampaignForm()}

            {campaigns.length === 0 && !showCreateForm ? (
              <div className="text-center py-16 text-gray-500">
                <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">No campaigns yet</p>
                <p className="text-xs mt-1">Connect your Meta Ads account and create your first campaign.</p>
                <button
                  onClick={handleOpenCreate}
                  className="mt-4 text-xs px-5 py-2.5 rounded-lg bg-purple-600 text-white hover:bg-purple-500 font-medium inline-flex items-center gap-1.5 transition-all"
                >
                  <Plus size={14} /> Create Campaign
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {!showCreateForm && campaigns.map(renderCampaignCard)}
              </div>
            )}
          </div>
        )}

        {activeTab === 'create' && (
          <>
            {!isConnected && (
              <div className="text-center py-12 text-gray-500">
                <Link className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">
                  {hasCredentials ? 'Connect your Meta Ads account first.' : 'Enter your Meta App credentials above first.'}
                </p>
              </div>
            )}
            {isConnected && renderCampaignForm()}
          </>
        )}

        {activeTab === 'metrics' && renderMetricsTab()}
      </div>
    </div>
  );
};

export default MetaAdsDashboard;
